/**
 * TrendingPlaces — real-time last-6-visited feed for WorldPulse landing.
 * Turns cold traffic into a live discovery flywheel.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Sparkles, MapPin } from "lucide-react";

const KIND_LABEL = {
  country:  "country",
  state:    "state",
  city:     "city",
  district: "district",
  village:  "village",
  town:     "town",
  county:   "county",
};

export default function TrendingPlaces({ accent = "#4f46e5" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await axios.get(`${API}/wp/trending?limit=6`);
        if (!cancelled) setItems(r.data.items || []);
      } catch { if (!cancelled) setItems([]); }
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    const t = setInterval(load, 30000);  // refresh every 30s
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (loading || !items.length) return null;

  return (
    <section
      className="bg-white border rounded-2xl p-5 sm:p-6"
      data-testid="wp-trending-places"
    >
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div
            className="text-xs uppercase tracking-[0.2em] mb-1 flex items-center gap-1.5"
            style={{ color: accent }}
          >
            <Sparkles className="w-3 h-3" />
            Trending right now
          </div>
          <h2 className="text-2xl font-semibold text-slate-900 tracking-tight">
            Places visitors are exploring
          </h2>
        </div>
        <span className="text-xs text-slate-400">refreshes every 30s</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {items.map((p) => (
          <Link
            key={p.location_id}
            to={`/wp/${p.location_slug}`}
            className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:shadow transition p-3"
            data-testid={`wp-trending-${p.location_id}`}
          >
            <div
              className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: `${accent}18`, color: accent }}
            >
              <MapPin className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 group-hover:underline truncate">
                {p.location_name}
              </div>
              <div className="text-[11px] text-slate-500 truncate">
                {KIND_LABEL[p.location_kind] || p.location_kind}
                {p.country && p.location_kind !== "country" ? ` · ${p.country}` : ""}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
